import { useMemo } from 'react';
import {
  ACCOUNT_DETAILS_MISMATCH,
  ERROR_MAPPING,
  ERROR_CODE_FIELDS,
} from 'Modules/patientfi/config/appStatusMap';
import { extractErrorCodes, stripErrorCodes } from 'Modules/patientfi/utils/errorCodes';

/**
 * Hook that extracts error configuration from an application object
 * @param {Object} application - The application object
 * @returns {Object} errorConfig - Object containing error type, status, message, errorCode, and affected fields
 */
export const useGetErrorConfig = (application) => {
  return useMemo(() => {
    if (!application || !application.payload || !application.payload.correction_required) {
      return {
        type: 'GENERIC',
        status: '',
        message: '',
        errorCode: null,
        affectedFields: [],
        requiresFullSsn: false,
      };
    }

    const { status = '', message = '' } = application.payload.correction_required;

    // Extract error codes from message if they exist in curly braces
    const errorCodes = extractErrorCodes(message);
    const errorCode = errorCodes[0] || null;

    // Clean the message by removing every error code part
    const cleanMessage = errorCode ? stripErrorCodes(message) : message;

    const error = ERROR_MAPPING.find((e) => e.status === status && e.message === cleanMessage);
    const codeFields =
      status === ACCOUNT_DETAILS_MISMATCH
        ? [...new Set(errorCodes.flatMap((code) => ERROR_CODE_FIELDS[code] || []))]
        : [];
    const errorType = error?.errorType || (codeFields.length ? status : 'GENERIC');

    return {
      type: errorType,
      status,
      message: cleanMessage ? cleanMessage : message,
      errorCode,
      affectedFields: error?.affectedFields || codeFields,
      requiresFullSsn:
        status === 'INCOMPLETE' &&
        (errorCodes.includes('SSN_MISMATCH') || Boolean(application.payload.existing_account)),
    };
  }, [application]);
};
